import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { isAuthenticated, getStoredUser, cleanupInvalidData } from "../api/auth";
import { getPublicStats } from "../api/public";
import "./Home.css";

export default function Home() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [stats, setStats] = useState({
    total_users: 0,
    partners_count: 0,
    agencies_count: 0,
    total_trips: 0,
    active_trips: 0,
    completed_trips: 0,
    top_destinations: [],
    total_connections: 0,
    total_ratings: 0,
    average_rating: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      // Clean up any invalid data first
      cleanupInvalidData();
      
      const loggedIn = isAuthenticated();
      setIsLoggedIn(loggedIn);
      if (loggedIn) {
        const profile = getStoredUser();
        // The stored profile has a nested 'user' object
        const user = profile?.user || profile;
        // Extract first name from full name
        const firstName = user?.name?.split(' ')[0] || "User";
        setUserName(firstName);
      }
    };
    checkAuth();
    loadStats();
  }, []);

  async function loadStats() {
    try {
      setStatsLoading(true);
      const data = await getPublicStats();
      setStats(data);
    } catch (err) {
      console.error("Error loading stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }

  // Function to get destination-specific image based on destination name
  function getDestinationImage(destinationName) {
    const destination = destinationName.toLowerCase();
    
    // Map popular destinations to specific images
    const destinationImages = {
      // Asian destinations
      'bali': 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'tokyo': 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'bangkok': 'https://images.unsplash.com/photo-1508009603885-50cf7c579365?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'singapore': 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'dubai': 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'maldives': 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'hong kong': 'https://images.unsplash.com/photo-1536599018102-9f803c140fc1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'seoul': 'https://images.unsplash.com/photo-1517154421773-0529f29ea451?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      
      // European destinations
      'paris': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'london': 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'rome': 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'barcelona': 'https://images.unsplash.com/photo-1583422409516-2895a77efded?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'amsterdam': 'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'venice': 'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'santorini': 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'iceland': 'https://images.unsplash.com/photo-1504829857797-ddff29c27927?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      
      // American destinations
      'new york': 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'los angeles': 'https://images.unsplash.com/photo-1534190239940-9ba8944ea261?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'san francisco': 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'miami': 'https://images.unsplash.com/photo-1506966953602-c20cc11f75e3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'cancun': 'https://images.unsplash.com/photo-1518509562904-e7ef99cdcc86?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'rio de janeiro': 'https://images.unsplash.com/photo-1483729558449-99ef09a8c325?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      
      // Other destinations
      'sydney': 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'melbourne': 'https://images.unsplash.com/photo-1514395462725-fb4566210144?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'new zealand': 'https://images.unsplash.com/photo-1507699622108-4be3abd695ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'cape town': 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'egypt': 'https://images.unsplash.com/photo-1572252009286-268acec5ca0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'morocco': 'https://images.unsplash.com/photo-1489749798305-4fea3ae63d43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    };
    
    // Try to find an exact match
    if (destinationImages[destination]) {
      return destinationImages[destination];
    }
    
    // Try to find a partial match
    for (const [key, url] of Object.entries(destinationImages)) {
      if (destination.includes(key) || key.includes(destination)) {
        return url;
      }
    }
    
    // Default fallback images if no match found
    const fallbackImages = [
      'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1488085061387-422e29b40080?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1500835556837-99ac94a94552?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    ];
    
    return fallbackImages[Math.floor(Math.random() * fallbackImages.length)];
  }

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <span className="hero-badge">🌍 Discover • Connect • Explore</span>
            {!isLoggedIn ? (
              <h1 className="hero-title">
                Find Your Perfect<br />
                <span className="gradient-text">Travel Buddy</span>
              </h1>
            ) : (
              <h1 className="hero-title">
                Welcome Back, <span className="gradient-text">{userName}!</span><br />
                <span style={{ fontSize: '0.7em' }}>Ready for Your Next Adventure?</span>
              </h1>
            )}
            <p className="hero-description">
              Connect with like-minded solo travelers, join amazing group trips by trusted agencies, 
              and explore the world together. Your next adventure starts here!
            </p>
            {!isLoggedIn ? (
              <div className="hero-buttons">
                <Link to="/register" className="btn btn-primary">
                  <span>Get Started</span>
                  <span className="btn-arrow">→</span>
                </Link>
                <Link to="/login" className="btn btn-secondary">
                  <span>Sign In</span>
                </Link>
              </div>
            ) : (
              <div className="hero-buttons">
                <Link to="/find-partner" className="btn btn-primary">
                  <span>Find Travel Partners</span>
                  <span className="btn-arrow">→</span>
                </Link>
                <Link to="/agencies" className="btn btn-secondary">
                  <span>Browse Agencies</span>
                </Link>
              </div>
            )}
            <div className="hero-stats">
              {!statsLoading && stats.total_users >= 50 && (
                <div className="stat">
                  <div className="stat-number">
                    {`${stats.total_users}+`}
                  </div>
                  <div className="stat-label">Travelers</div>
                </div>
              )}
              {!statsLoading && stats.total_connections >= 50 && (
                <div className="stat">
                  <div className="stat-number">
                    {`${stats.total_connections}+`}
                  </div>
                  <div className="stat-label">Connections</div>
                </div>
              )}
              {!statsLoading && stats.total_trips >= 50 && (
                <div className="stat">
                  <div className="stat-number">
                    {`${stats.total_trips}+`}
                  </div>
                  <div className="stat-label">Trips</div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="scroll-indicator">
          <span>Scroll to explore</span>
          <div className="scroll-arrow">↓</div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Go With Me Buddy?</h2>
            <p className="section-subtitle">Everything you need for the perfect travel experience</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🤝</div>
              <h3 className="feature-title">Find Travel Partners</h3>
              <p className="feature-description">
                Connect with verified travelers who share your interests and destination preferences
              </p>
              <Link to="/find-partner" className="feature-link">
                Explore Partners →
              </Link>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🏢</div>
              <h3 className="feature-title">Trusted Agencies</h3>
              <p className="feature-description">
                Browse curated group tours from verified travel agencies across the globe
              </p>
              <Link to="/agencies" className="feature-link">
                View Agencies →
              </Link>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🗺️</div>
              <h3 className="feature-title">Plan Together</h3>
              <p className="feature-description">
                Collaborate on itineraries, share expenses, and make unforgettable memories
              </p>
              {!isLoggedIn ? (
                <Link to="/register" className="feature-link">
                  Start Planning →
                </Link>
              ) : (
                <Link to="/find-partner" className="feature-link">
                  Find Partners →
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Destinations Section */}
      <section className="destinations-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Popular Destinations</h2>
            <p className="section-subtitle">Trending places our community loves to explore</p>
          </div>
          {statsLoading ? (
            <div className="destinations-loading">Loading destinations...</div>
          ) : stats.top_destinations && stats.top_destinations.length >= 2 ? (
            <div className="destinations-grid">
              {stats.top_destinations.slice(0, 4).map((dest, index) => {
                const imageUrl = getDestinationImage(dest.destination);
                return (
                  <div key={index} className="destination-card" style={{backgroundImage: `url('${imageUrl}')`}}>
                    <div className="destination-overlay"></div>
                    <div className="destination-content">
                      <h3 className="destination-name">{dest.destination}</h3>
                      <p className="destination-info">✈️ Popular Choice</p>
                      {dest.trip_count >= 50 && (
                        <div className="destination-badge">{dest.trip_count} Trips</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="destinations-grid">
              <div className="destination-card" style={{backgroundImage: "url('https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80')"}}>
                <div className="destination-overlay"></div>
                <div className="destination-content">
                  <h3 className="destination-name">Adventure Awaits</h3>
                  <p className="destination-info">🌎 Discover</p>
                  <div className="destination-badge">Start Exploring</div>
                </div>
              </div>
              <div className="destination-card" style={{backgroundImage: "url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80')"}}>
                <div className="destination-overlay"></div>
                <div className="destination-content">
                  <h3 className="destination-name">Explore the World</h3>
                  <p className="destination-info">🗺️ Journey</p>
                  <div className="destination-badge">Your Next Trip</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      {!isLoggedIn && (
        <section className="cta-section">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Start Your Adventure?</h2>
            <p className="cta-description">
              Join thousands of travelers already exploring the world with Go With Me Buddy
            </p>
            <Link to="/register" className="btn btn-cta">
              <span>Create Free Account</span>
              <span className="btn-arrow">→</span>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}